import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Code as VariableIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Sync as SyncIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  ChatBubbleOutline as PromptIcon,
} from '@mui/icons-material';
import promptService, { Prompt } from '../../services/prompt.service';
import { getPromptVariables } from './promptVariables';
import LearningLoopGuide from './LearningLoopGuide';
import { LOOP_GROUP, LOOP_GROUP_LABEL, LOOP_NETWORKS, LOOP_PROMPT_BY_SLUG, pNo, type LoopNetwork } from './loopPromptMeta';
import { List, ListItemButton, ListSubheader, Collapse, Divider } from '@mui/material';
import { ExpandLess, ExpandMore, Folder as FolderIcon, AutoStories as GuideIcon } from '@mui/icons-material';

const DefaultPrompts: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Prompt list + selection
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [promptsLoading, setPromptsLoading] = useState<boolean>(true);
  const [promptsError, setPromptsError] = useState<string | null>(null);
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [promptSlug, setPromptSlug] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  // Folders in the list: the learning loop (numbered, with a "How it works" page) and everything else.
  const [loopOpen, setLoopOpen] = useState<boolean>(true);
  const GUIDE_ID = 'learning-loop-guide';

  // Editor
  const [prompt, setPrompt] = useState<string>('');
  const [promptLoading, setPromptLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);

  // Prompts are read-only until Edit is clicked; the backup restores on Cancel
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [promptBackup, setPromptBackup] = useState<string>('');

  // Reset confirmation
  const [resetDialogOpen, setResetDialogOpen] = useState<boolean>(false);

  // Variable menu
  const [variableAnchorEl, setVariableAnchorEl] = useState<null | HTMLElement>(null);
  const textFieldRef = React.useRef<HTMLInputElement>(null);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------------

  const fetchDefaultPrompts = async (keepSelection = true) => {
    setPromptsError(null);
    setPromptsLoading(true);

    try {
      const response = await promptService.getCompanyPrompts({ is_default_prompt: true });
      const promptsData: any = response.data || [];
      setPrompts(promptsData);

      if (promptsData.length > 0) {
        // Keep whatever the user had open if it still exists, else fall back to the first
        const current = keepSelection
          ? promptsData.find((p: any) => p._id === selectedPromptId)
          : null;
        const target = current || promptsData[0];

        setSelectedPromptId(target._id);
        setPrompt(target.prompt || '');
        setPromptSlug(target.braintrust_slug || '');
      } else {
        setSelectedPromptId('');
        setPrompt('');
        setPromptSlug('');
      }
    } catch (err: any) {
      setPromptsError(err?.response?.data?.message || 'Failed to fetch prompts');
      console.error('Error fetching default prompts:', err);
    } finally {
      setPromptsLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultPrompts(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecting another prompt drops any in-progress edit
  const handleSelectPrompt = (_event: any, newValue: string) => {
    setSelectedPromptId(newValue);
    setIsEditing(false);
    if (newValue === GUIDE_ID) { setPrompt(''); setPromptSlug(''); return; }

    const selected: any = prompts.find((p: any) => p._id === newValue);
    if (selected) {
      setPrompt(selected.prompt || '');
      setPromptSlug(selected.braintrust_slug || '');
    } else {
      setPrompt('');
      setPromptSlug('');
    }
  };

  const startEditing = () => {
    setPromptBackup(prompt);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setPrompt(promptBackup);
    setIsEditing(false);
  };

  const handlePromptSubmit = async () => {
    if (!prompt.trim() || !selectedPromptId) return;

    setPromptLoading(true);

    try {
      const payload = {
        "id": selectedPromptId,
        "prompt": prompt,
        "type": selectedPromptId,
        "company_id": "",
        "project_id": "",
        "round_id": ""
      };
      await promptService.editPrompt(payload);
      setIsEditing(false);
      await fetchDefaultPrompts();
      showSnackbar('Prompt saved successfully', 'success');
    } catch (err: any) {
      console.error('Error saving prompt:', err);
      showSnackbar('Failed to save prompt', 'error');
    } finally {
      setPromptLoading(false);
    }
  };

  const handleSyncBraintrust = async () => {
    setSyncLoading(true);
    try {
      await promptService.updatePrompts();
      await fetchDefaultPrompts();
      showSnackbar('Prompts synced with Braintrust successfully', 'success');
    } catch (err) {
      console.error('Error syncing with braintrust:', err);
      showSnackbar('Failed to sync with Braintrust', 'error');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    setResetDialogOpen(false);

    try {
      if (!selectedPromptId) return;
      await promptService.getResetPrompt(selectedPromptId);
      setIsEditing(false);
      await fetchDefaultPrompts();
      showSnackbar('Prompt reset successfully', 'success');
    } catch (err: any) {
      console.error('Error resetting prompt:', err);
      showSnackbar('Failed to reset prompt', 'error');
    }
  };

  // Variables offered in the editor depend on the prompt's Braintrust slug
  const activeVariables = getPromptVariables(promptSlug);

  const handleVariableMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setVariableAnchorEl(event.currentTarget);
  };

  const handleVariableMenuClose = () => {
    setVariableAnchorEl(null);
  };

  const insertVariable = (variable: string) => {
    const input = textFieldRef.current;

    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;

      setPrompt(currentValue.substring(0, start) + variable + currentValue.substring(end));

      // Put the caret back where the user was, just after what we inserted
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }

    handleVariableMenuClose();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const filteredPrompts = prompts.filter((item: any) => {
    if (!search.trim()) return true;
    const needle = search.trim().toLowerCase();
    return (
      (item.title || '').toLowerCase().includes(needle) ||
      (item.braintrust_slug || '').toLowerCase().includes(needle)
    );
  });

  // Learning-loop prompts are recognised by their group or, before the first
  // sync after the upgrade, by their Braintrust slug.
  const isLoop = (item: any) => item.group === LOOP_GROUP || !!LOOP_PROMPT_BY_SLUG[item.braintrust_slug || ''];
  const loopOrder = (item: any) => Number(item.sort_order) || LOOP_PROMPT_BY_SLUG[item.braintrust_slug || '']?.number || 999;
  const loopPrompts = filteredPrompts.filter(isLoop).sort((a: any, b: any) => loopOrder(a) - loopOrder(b));
  const otherPrompts = filteredPrompts.filter((p: any) => !isLoop(p));
  // The folder in three parts: the X loop, the LinkedIn loop, and what both use.
  // The backend stores `network` on sync; until then the local list decides.
  const loopNetwork = (item: any): LoopNetwork => {
    const n = item.network || LOOP_PROMPT_BY_SLUG[item.braintrust_slug || '']?.network;
    return n === 'x' || n === 'linkedin' ? n : 'both';
  };
  const loopSections = LOOP_NETWORKS.map(net => ({ ...net, items: loopPrompts.filter((p: any) => loopNetwork(p) === net.key) })).filter(sec => sec.items.length > 0);
  const slugToId: Record<string, string> = Object.fromEntries(prompts.filter((p: any) => p.braintrust_slug).map((p: any) => [p.braintrust_slug, p._id]));
  const loopLabel = (item: any) => {
    const meta = LOOP_PROMPT_BY_SLUG[item.braintrust_slug || ''];
    const n = Number(item.sort_order) || meta?.number;
    const title = String(item.title || '').replace(/^P\d+\s*·\s*/, '');
    return n ? `${pNo(n)} · ${meta?.name || title}` : title;
  };
  const openPromptById = (id: string) => { handleSelectPrompt(null, id); };
  const showingGuide = selectedPromptId === GUIDE_ID;

  const selectedPrompt: any = prompts.find((p: any) => p._id === selectedPromptId);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          Default Prompts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Default AI prompts used across every company. The Learning loop folder holds every prompt of the vodcast → social loop, numbered in the order they run, with a page that shows where each one is used.
        </Typography>
      </Box>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, '&:hover': { transform: 'none' } }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <PromptIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" fontWeight={700}>
              Default Prompts
            </Typography>
            {prompts.length > 0 && (
              <Chip size="small" label={prompts.length} sx={{ fontWeight: 600 }} />
            )}
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={syncLoading ? undefined : <SyncIcon />}
            onClick={handleSyncBraintrust}
            disabled={syncLoading}
            sx={{ minWidth: 170 }}
          >
            {syncLoading ? <CircularProgress size={20} /> : 'Sync with Braintrust'}
          </Button>
        </Box>

        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {promptsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : promptsError ? (
            <Alert severity="error">{promptsError}</Alert>
          ) : prompts.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 3,
                borderRadius: 3,
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                No default prompts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sync with Braintrust to pull the default prompt templates.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, md: 3 },
                alignItems: { xs: 'flex-start', md: 'stretch' },
              }}
            >
              {/* Left sidebar: every default prompt */}
              <Box
                sx={{
                  width: { xs: '100%', md: 260 },
                  flexShrink: 0,
                  borderRight: { xs: 0, md: `1px solid ${theme.palette.divider}` },
                  pr: { xs: 0, md: 2 },
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search prompts"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 1.5 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {filteredPrompts.length === 0 && !search.trim() ? null : filteredPrompts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                    No prompt matches "{search}".
                  </Typography>
                ) : null}
                <List dense disablePadding sx={{ maxHeight: { xs: 260, md: 640 }, overflowY: 'auto',
                  '& .MuiListItemButton-root': { borderRadius: 1.5, mb: 0.25, px: 1.5, minHeight: 38, textTransform: 'none' },
                  '& .Mui-selected': { bgcolor: `${alpha(theme.palette.primary.main, 0.08)} !important`, fontWeight: 700 } }}>
                  {/* Everything else: directly in the list, no folder */}
                  {otherPrompts.map((item: any) => (
                    <ListItemButton key={item._id} selected={item._id === selectedPromptId} onClick={() => handleSelectPrompt(null, item._id)}>
                      <Box sx={{ fontSize: 13, lineHeight: 1.3, textTransform: 'capitalize' }}>{item.title}</Box>
                    </ListItemButton>
                  ))}
                  {otherPrompts.length > 0 && <Divider sx={{ my: 1 }} />}
                  {/* Learning loop folder */}
                  <ListSubheader disableSticky component="div" sx={{ px: 0, lineHeight: 1, bgcolor: 'transparent' }}>
                    <ListItemButton onClick={() => setLoopOpen(o => !o)} sx={{ fontWeight: 700, color: 'text.primary' }}>
                      <FolderIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Box sx={{ flexGrow: 1 }}>{LOOP_GROUP_LABEL}</Box>
                      <Chip size="small" label={loopPrompts.length} sx={{ mr: 1, height: 20 }} />
                      {loopOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </ListItemButton>
                  </ListSubheader>
                  <Collapse in={loopOpen} timeout="auto" unmountOnExit>
                    <ListItemButton selected={showingGuide} onClick={() => handleSelectPrompt(null, GUIDE_ID)} sx={{ pl: 3 }}>
                      <GuideIcon fontSize="small" sx={{ mr: 1, color: theme.palette.secondary.main }} />
                      <Box sx={{ fontWeight: 600 }}>How it works</Box>
                    </ListItemButton>
                    {loopSections.map(sec => (
                      <Box key={sec.key} component="section" aria-label={sec.label}>
                        <Tooltip title={sec.hint} placement="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3, pr: 1.5, pt: 1.5, pb: 0.5 }}>
                            <Typography component="h3" sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', flexGrow: 1 }}>{sec.label}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{sec.items.length}</Typography>
                          </Box>
                        </Tooltip>
                        {sec.items.map((item: any) => (
                          <ListItemButton key={item._id} selected={item._id === selectedPromptId} onClick={() => handleSelectPrompt(null, item._id)} sx={{ pl: 4 }}>
                            <Box sx={{ fontSize: 13, lineHeight: 1.3 }}>{loopLabel(item)}</Box>
                          </ListItemButton>
                        ))}
                      </Box>
                    ))}
                    {loopPrompts.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 3, display: 'block', py: 1 }}>Sync with Braintrust to pull the loop prompts.</Typography>
                    )}
                  </Collapse>

                </List>
              </Box>

              {/* Right pane: the selected prompt, read-only until Edit */}
              <Box sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}>
                {showingGuide ? (
                  <LearningLoopGuide slugToId={slugToId} onOpenPrompt={openPromptById} />
                ) : (
                <>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ textTransform: 'none' }}>
                      {selectedPrompt && isLoop(selectedPrompt) ? loopLabel(selectedPrompt) : (selectedPrompt?.title || 'Prompt')}
                    </Typography>
                    {promptSlug && (
                      <Tooltip title="Braintrust slug">
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<VariableIcon fontSize="small" />}
                          label={promptSlug}
                          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        />
                      </Tooltip>
                    )}
                    {isEditing && activeVariables.length > 0 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VariableIcon />}
                        endIcon={<ArrowDownIcon />}
                        onClick={handleVariableMenuOpen}
                      >
                        Variables
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {isEditing ? (
                      <>
                        <Button
                          variant="outlined"
                          color="inherit"
                          startIcon={<CloseIcon />}
                          onClick={cancelEditing}
                          disabled={promptLoading}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={promptLoading ? undefined : <SendIcon />}
                          onClick={handlePromptSubmit}
                          disabled={!prompt?.trim() || promptLoading}
                          sx={{ minWidth: 110 }}
                        >
                          {promptLoading ? <CircularProgress size={22} color="inherit" /> : 'Save'}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<RefreshIcon />}
                          onClick={() => setResetDialogOpen(true)}
                          disabled={!selectedPromptId}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={startEditing}
                          disabled={!selectedPromptId}
                          sx={{ minWidth: 110 }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>

                {selectedPrompt && (selectedPrompt.description || LOOP_PROMPT_BY_SLUG[selectedPrompt.braintrust_slug || '']) && (
                  <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
                    <Typography variant="body2">{selectedPrompt.description || LOOP_PROMPT_BY_SLUG[selectedPrompt.braintrust_slug || '']?.role}</Typography>
                    {LOOP_PROMPT_BY_SLUG[selectedPrompt.braintrust_slug || '']?.when && (
                      <Typography variant="caption" color="text.secondary"><b>When it runs:</b> {LOOP_PROMPT_BY_SLUG[selectedPrompt.braintrust_slug || '']?.when}</Typography>
                    )}
                  </Alert>
                )}
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={20}
                    placeholder="Enter your prompt here..."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    disabled={promptLoading}
                    inputRef={textFieldRef}
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: alpha(theme.palette.text.primary, 0.02),
                        alignItems: 'flex-start',
                      },
                      '& .MuiInputBase-inputMultiline': {
                        resize: 'vertical',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 13,
                        lineHeight: 1.7,
                      },
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.text.primary, 0.02),
                      px: 1.75,
                      py: 1.5,
                      height: 620,
                      overflowY: 'auto',
                    }}
                  >
                    {prompt?.trim() ? (
                      <Typography
                        component="pre"
                        sx={{
                          m: 0,
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                          fontSize: 13,
                          lineHeight: 1.7,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {prompt}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No prompt set yet. Click Edit to add one.
                      </Typography>
                    )}
                  </Box>
                )}
                </>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to Reset this Prompt? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmReset} color="error" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Variable Insertion Menu */}
      <Menu
        anchorEl={variableAnchorEl}
        open={Boolean(variableAnchorEl)}
        onClose={handleVariableMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxHeight: 380, minWidth: 260 } } }}
      >
        {activeVariables.map((variable, index) => (
          <MenuItem key={index} onClick={() => insertVariable(variable.value)}>
            <ListItemIcon>
              <VariableIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={variable.name}
              secondary={variable.value}
              slotProps={{ secondary: { sx: { fontFamily: 'monospace', fontSize: 12 } } }}
            />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DefaultPrompts;
